import React, { useState } from 'react';
import {
  Text,
  DataGrid,
  useResponsiveParams,
  Paper,
  Grid,
  GridItem,
  Button,
  TextArea,
  Label,
} from 'quanta-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faPaperclip,
} from '@fortawesome/free-solid-svg-icons';
import ModalViewDefault from './modal-view-defaults';

export default function AdditionalDetailsSummary({
  fireQuote,
  fireQuoteUploadDocs,
  goToStep,
  productCode,
  role,
  underWriterComment,
  setUnderWriterComment,
}) {
  const [showViewDefault, setShowDefaultDetails] = useState(false);
  const repeatForMobile = 1;
  const repeatForDesktop = 3;
  const ccweRepeatForMobile = 1;
  const ccweRepeatForDesktop = 4;
  const repeatForMobileAdditionalDetails = 1;
  const repeatForDesktopAdditionalDetails = 3;

  const screenType = useResponsiveParams({
    for_desktop: 'desktop',
    for_mobile: 'mobile',
    for_iPad: 'ipad',
  });

  const repeat = useResponsiveParams({
    for_mobile: repeatForMobile,
    for_desktop: repeatForDesktop,
  });

  const ccweRepeat = useResponsiveParams({
    for_mobile: ccweRepeatForMobile,
    for_desktop: ccweRepeatForDesktop,
  });
  const repeatAdditionalDetails = useResponsiveParams({
    for_mobile: repeatForMobileAdditionalDetails,
    for_desktop: repeatForDesktopAdditionalDetails,
  });

  const uploadDocsData = [
    {
      label: '',
      colSpan: repeat,
      className: 'my-4',
      text: fireQuoteUploadDocs?.documentDetails
        ?.filter(
          (doc) => doc?.documentName && doc?.documentBase64String,
        )
        ?.map((doc, idx) => {
          return (
            <Paper
              key={idx}
              className={`my-4 ${
                idx === 0 ? '-mt-1' : ''
              } bg-neutral-light border border-neutral p-4 rounded`}
            >
              <Grid repeat={repeat} gap={0} gapX={0} gapY={0}>
                <GridItem
                  colSpan={
                    screenType?.toString()?.toLowerCase() === 'mobile'
                      ? 1
                      : 2
                  }
                >
                  <Text>
                    <Text>
                      <FontAwesomeIcon
                        icon={faPaperclip}
                        size="1x"
                        className="mr-3"
                      />
                    </Text>
                    <Text
                      severity={'info'}
                      className={'font-semibold'}
                    >
                      {doc?.documentDescription &&
                      doc?.documentName?.toString()?.toLowerCase() ===
                        'others'
                        ? doc?.documentDescription
                        : doc?.documentName}
                    </Text>
                    <Text
                      className={'flex flex-col ml-7'}
                      severity={'neutral-darker'}
                      type={'label'}
                    >
                      <Text className={'flex items-center flex-wrap'}>
                        <Text type={'label'}>File Name:&nbsp;</Text>
                        <Text
                          type={'label'}
                          className={'break-normal'}
                        >
                          {doc?.documentFileNameWithExtension}
                        </Text>
                      </Text>
                    </Text>
                    <Text
                      className={'flex flex-col ml-7'}
                      severity={'neutral-darker'}
                      type={'label'}
                    >
                      File Size:&nbsp;
                      {doc?.documentSize} KB
                    </Text>
                  </Text>
                </GridItem>
                <GridItem
                  colSpan={
                    screenType?.toString()?.toLowerCase() === 'mobile'
                      ? repeat
                      : 1
                  }
                  className={'flex flex-col justify-center items-end'}
                >
                  <Text className="flex justify-end items-center">
                    <Button
                      size={'small'}
                      type={'button'}
                      flat={true}
                      className="w-full md:w-56 md:pr-4 max-h-10 -ml-0.5"
                      onClick={(_) => {
                        const downloadLink =
                          document.createElement('a');
                        const fileName =
                          doc?.documentFileNameWithExtension;
                        downloadLink.href = doc?.documentBase64String;
                        downloadLink.download = fileName;
                        downloadLink.click();
                      }}
                      iconPre={<FontAwesomeIcon icon={faDownload} />}
                    >
                      Download
                    </Button>
                  </Text>
                </GridItem>
              </Grid>
            </Paper>
          );
        }),
    },
  ];
  const underwriterData = [
    {
      colSpan: repeatAdditionalDetails,
      text: (
        <Grid
          className="mt-3 w-full"
          flow="cols"
          gap={2}
          repeat={repeatAdditionalDetails}
        >
          <GridItem
            colSpan={repeatAdditionalDetails}
            className={'w-full'}
          >
            <Text
              className={
                screenType?.toString()?.toLowerCase() === 'mobile'
                  ? 'w-full'
                  : 'flex flex-row w-full space-x-5'
              }
            >
              <Text className={'w-full'}>
                <Label
                  text={
                    <Text type={'label'}>
                      Comments <Text severity={'error'}>*</Text>
                    </Text>
                  }
                >
                  <TextArea
                    type={'textarea'}
                    value={underWriterComment}
                    size={'full'}
                    colSpan={repeatAdditionalDetails}
                    rows={2}
                    disabled={
                      fireQuote?.status?.toString()?.toLowerCase() ===
                        'underwriter rejected' ||
                      fireQuote?.status?.toString()?.toLowerCase() ===
                        'underwriter approved' ||
                      fireQuote?.status?.toString()?.toLowerCase() ===
                        'converted to proposal'
                    }
                    className={
                      screenType?.toString()?.toLowerCase() ===
                      'mobile'
                        ? 'w-full'
                        : 'w-full'
                    }
                    onChange={(e) => {
                      setUnderWriterComment(e.target.value);
                    }}
                  />
                </Label>
              </Text>
            </Text>
          </GridItem>
        </Grid>
      ),
    },
  ];
  const underwriterDataProps = {
    flow: 'cols',
    repeat: repeatAdditionalDetails,
    gapX: 6,
    gapY: 6,
    spaceY: 2,
    size: 'full',
    titleSeverity: 'info',
  };
  return (
    <Text className={'flex flex-col space-y-4'}>
      <Text
        className={
          'flex space-x-2 items-center w-full whitespace-nowrap'
        }
      >
        <Text severity={'info'} type={'title'}>
          Additional Details
        </Text>
        <Text className={'flex items-center h-full w-full'}>
          {[
            'quote saved',
            'underwriter approved',
            'underwriter rejected',
            'converted to proposal',
          ]?.includes(fireQuote?.status?.toString()?.toLowerCase()) ||
          (fireQuote?.status?.toString()?.toLowerCase() ===
            'pending uw approval' &&
            role?.toString()?.toLowerCase() !== 'underwriter') ? (
            <Text
              type={'label'}
              className={
                'quanta_link flex flex-col justify-center items-center -mb-0.5'
              }
              onClick={(_) => goToStep(2)}
            >
              View Details
            </Text>
          ) : (
            <Button
              flat
              type={'button'}
              className={`w-16 max-h-8`}
              size={'small'}
              onClick={(e) => {
                goToStep(2);
              }}
            >
              Change
            </Button>
          )}
        </Text>
      </Text>

      {fireQuoteUploadDocs?.documentDetails?.filter(
        (docDet) =>
          docDet?.documentBase64String && docDet?.documentName,
      )?.length > 0 && (
        <>
          <Text severity={'info'} className={'text-lg'}>
            Uploaded Documents
          </Text>
          <DataGrid
            gap={2}
            gapY={2}
            repeat={repeat}
            data={uploadDocsData}
            textType="normal"
            nullData={() => {
              return (
                <Text type="normal" severity="neutral">
                  Not Captured
                </Text>
              );
            }}
          />
        </>
      )}
      <Text className={'flex space-x-2 items-center'}>
        <Text severity={'info'} className={'text-lg'}>
          Clauses, Conditions , Warranties and Exclusions
        </Text>
        <Text
          type={'label'}
          className={
            'quanta_link flex flex-col justify-center items-center -mb-0.5'
          }
          onClick={(_) => {
            setShowDefaultDetails(true);
          }}
        >
          View Defaults
        </Text>
      </Text>
      <Text>
        
          <GridItem colSpan={1} className={'flex flex-col space-y-2'}>
            <>
              <Text severity={'info'}>Clauses</Text>
              <Text>
                <ul className={'-mt-2'}>
                  {fireQuote?.tabDetails?.additionalTabDetails
                    ?.clauses?.userDefinedClauses?.length === 0 ? (
                    <Text severity={'neutral'}>Not Captured</Text>
                  ) : (
                    fireQuote?.tabDetails?.additionalTabDetails?.clauses?.userDefinedClauses?.map(
                      (c, idx) => {
                        return (
                          <li key={idx} className={'break-all my-1'}>
                            • {c}
                          </li>
                        );
                      },
                    )
                  )}
                </ul>
              </Text>
            </>
          </GridItem>
        </Text>
        <Text>
          <GridItem colSpan={1} className={'flex flex-col space-y-2'}>
            <>
              <Text severity={'info'}>Conditions</Text>
              <Text>
                <ul className={'-mt-2'}>
                  {fireQuote?.tabDetails?.additionalTabDetails
                    ?.conditions?.userDefinedConditions?.length ===
                  0 ? (
                    <Text severity={'neutral'}>Not Captured</Text>
                  ) : (
                    fireQuote?.tabDetails?.additionalTabDetails?.conditions?.userDefinedConditions?.map(
                      (c, idx) => {
                        return (
                          <li key={idx} className={'break-all my-1'}>
                            • {c}
                          </li>
                        );
                      },
                    )
                  )}
                </ul>
              </Text>
            </>
          </GridItem>
          </Text>
          <Text>
          <GridItem colSpan={1} className={'flex flex-col space-y-2'}>
            <>
              <Text severity={'info'}>Warranties</Text>
              <Text>
                <ul className={'-mt-2'}>
                  {fireQuote?.tabDetails?.additionalTabDetails
                    ?.warranties?.userDefinedWarranties?.length ===
                  0 ? (
                    <Text severity={'neutral'}>Not Captured</Text>
                  ) : (
                    fireQuote?.tabDetails?.additionalTabDetails?.warranties?.userDefinedWarranties?.map(
                      (w, idx) => {
                        return (
                          <li key={idx} className={'break-all my-1'}>
                            • {w}
                          </li>
                        );
                      },
                    )
                  )}
                </ul>
              </Text>
            </>
          </GridItem>
          </Text>
          <Text>
          <GridItem colSpan={1} className={'flex flex-col space-y-2'}>
            <>
              <Text severity={'info'}>Exclusions</Text>
              <Text>
                <ul className={'-mt-2'}>
                  {fireQuote?.tabDetails?.additionalTabDetails
                    ?.exclusions?.userDefinedExclusions?.length ===
                  0 ? (
                    <Text severity={'neutral'}>Not Caputured</Text>
                  ) : (
                    fireQuote?.tabDetails?.additionalTabDetails?.exclusions?.userDefinedExclusions?.map(
                      (e, idx) => {
                        return (
                          <li key={idx} className={'break-all my-1'}>
                            • {e}
                          </li>
                        );
                      },
                    )
                  )}
                </ul>
              </Text>
            </>
          </GridItem>
      </Text>
      {((role?.toString()?.toLowerCase() === 'underwriter' && 
      fireQuote?.status?.toString()?.toLowerCase() === 'pending uw approval') || (underWriterComment ||
        fireQuote?.tabDetails?.additionalTabDetails
          ?.underwriterComment)) && <>
        <Text severity={'info'} type={'title'}>
            Underwriter Comments
          </Text>
        {role?.toString()?.toLowerCase() === 'underwriter' &&
        fireQuote?.status?.toString()?.toLowerCase() ===
          'pending uw approval' ? (
          <Paper>
            <DataGrid
              {...{
                data: underwriterData,
                ...underwriterDataProps,
              }}
            />
          </Paper>
        ) : (
          <Text className={'w-full break-all'}>
            {underWriterComment ||
              fireQuote?.tabDetails?.additionalTabDetails
                ?.underwriterComment}
          </Text>
        )}
        {/* <Text className={'w-full break-all'}>
            {
              fireQuote?.tabDetails?.additionalTabDetails
                ?.underwriterComment
            }
          </Text> */}
      </>}
      <div>
        <ModalViewDefault
          {...{ showViewDefault, setShowDefaultDetails, productCode }}
        />
      </div>
    </Text>
  );
}
